/* eslint-disable import/prefer-default-export */
import { ref, watch } from '@vue/composition-api'
import moment from 'moment'
import {
  createPlanningPayloadFromEvent,
  createDurationPatchPayload,
  createAssignmentOffsetPatchPayload,
  createPlannerOrderWithOverrides,
  mapPlannerOrderToEvent,
  resolveEventDurationMinutes,
  eventHasMeaningfulChange,
  eventHasTimeChange,
  uniqueNumericIds,
} from '@/views/orders/Planner/setup'

export const usePlannerEvents = (plannerData, deps) => {
  const { goToOrderView, planningModal } = deps

  const selectedEvent = ref(null)
  const selectedEventAnchor = ref(null)
  const isEventPopupOpen = ref(false)
  const dragTimePendingModal = ref(false)
  const pendingPlannerDropOrder = ref(null)

  const closeEventPopup = () => {
    isEventPopupOpen.value = false
    selectedEventAnchor.value = null
    selectedEvent.value = null
  }

  watch(
    () => plannerData.plannerState.value.events,
    events => {
      if (!selectedEvent.value) {
        return
      }
      const currentOrderId = String(selectedEvent.value.orderId || selectedEvent.value.original?.orderId)
      const next = events.find(
        event => String(event.orderId || event.original?.orderId) === currentOrderId,
      )
      selectedEvent.value = next || null
      if (!selectedEvent.value) {
        closeEventPopup()
      }
    },
  )

  const handleEventClicked = ({ event, target }) => {
    const resolved = {
      ...event.original,
      ...event,
      orderId: event.original?.orderId || event.orderId,
    }
    if (resolved.isAssigned) {
      goToOrderView(resolved.orderId)
      return
    }
    selectedEvent.value = resolved
    selectedEventAnchor.value = target
    isEventPopupOpen.value = true
  }

  const resolveTeamDefinitionObject = id => {
    const normalizedId = Number(id || 0)
    if (!normalizedId) {
      return null
    }
    return plannerData.plannerState.value.teamDefinitions.find(
      td => Number(td?.id) === normalizedId,
    ) || { id: normalizedId }
  }

  const resolveTeamDefinitionObjects = teamDefinitionIds => {
    const ids = uniqueNumericIds(teamDefinitionIds)
    return ids.map(id => resolveTeamDefinitionObject(id))
  }

  const resolveEventWithSelectedTeamDefinition = event => {
    const teamDefinition = resolveTeamDefinitionObject(event?.resource)
    if (!teamDefinition || event?.original?.isFirstAvailablePlanning) {
      return event
    }
    return {
      ...event,
      original: {
        ...(event?.original || {}),
        plannedTeamDefinition: teamDefinition,
      },
    }
  }

  const resolveFirstAvailableDragTeamDefinitionIds = ({ event, oldEvent }) => {
    const originalTeamDefinitions = Array.isArray(event?.original?.plannedTeamDefinitions)
      ? event.original.plannedTeamDefinitions
      : []
    const originalIds = originalTeamDefinitions.map(td => Number(td?.id)).filter(Boolean)
    const fallbackIds = event?.original?.plannedTeamDefinition?.id
      ? [Number(event.original.plannedTeamDefinition.id)]
      : []
    const baseIds = originalIds.length ? originalIds : fallbackIds
    const oldResource = Number(oldEvent?.resource || 0)
    const newResource = Number(event?.resource || 0)

    if (!oldResource || !newResource || oldResource === newResource) {
      return uniqueNumericIds(baseIds.length ? baseIds : [newResource])
    }

    const replacedIds = baseIds.map(id => (id === oldResource ? newResource : id))
    const nextIds = replacedIds.some(id => id === newResource)
      ? replacedIds
      : [...replacedIds, newResource]
    return uniqueNumericIds(nextIds)
  }

  const hasFirstAvailableTeamChange = ({ event, oldEvent }) => {
    const oldResource = Number(oldEvent?.resource || 0)
    const newResource = Number(event?.resource || 0)
    return oldResource !== newResource && oldResource && newResource
  }

  const createFirstAvailablePlanningPayload = (event, teamDefinitionIds) => {
    const normalizedIds = uniqueNumericIds(teamDefinitionIds)
    if (!normalizedIds.length) {
      return null
    }
    return {
      firstAvailable: true,
      teamDefinitions: normalizedIds,
      assignmentOffsetMinutes: Number(event?.original?.bufferBefore || 0),
      durationMinutes: resolveEventDurationMinutes(event),
    }
  }

  const createFirstAvailableOptimisticEvent = (event, teamDefinitionIds) => {
    const teamDefinitions = resolveTeamDefinitionObjects(teamDefinitionIds)
    const currentResource = Number(event?.resource || 0)
    const visibleResource = teamDefinitions.some(td => Number(td?.id) === currentResource)
      ? currentResource
      : Number(teamDefinitions[0]?.id || 0)
    return {
      ...event,
      resource: visibleResource ? String(visibleResource) : event?.resource,
      original: {
        ...(event?.original || {}),
        isFirstAvailablePlanning: true,
        plannedTeamDefinitions: teamDefinitions,
        plannedTeamDefinition: visibleResource
          ? teamDefinitions.find(td => Number(td?.id) === visibleResource) || { id: visibleResource }
          : event?.original?.plannedTeamDefinition,
      },
    }
  }

  const openPlanningModalForDragTimeChange = (event, options = {}) => {
    const modal = planningModal?.value
    if (!modal || typeof modal.openForPlannerDrop !== 'function') {
      plannerData.fetchPlannerData()
      return
    }
    const teamDefinitionIds = uniqueNumericIds(options.teamDefinitionIds)
    const firstAvailableOverrides = teamDefinitionIds.length > 1
      ? {
        isFirstAvailablePlanning: true,
        plannedTeamDefinitions: resolveTeamDefinitionObjects(teamDefinitionIds),
      }
      : {}
    const order = {
      ...event.original,
      id: event.original?.orderId,
      plannedAssignmentOffsetMinutes: event.original?.bufferBefore ?? null,
      plannedDurationMinutes: event.original?.durationMinutes ?? null,
      ...firstAvailableOverrides,
    }
    dragTimePendingModal.value = true
    const modalTeamDefinitionId = teamDefinitionIds.length <= 1 && event.resource
      ? Number(event.resource)
      : null
    if (modalTeamDefinitionId) {
      order.plannedTeamDefinition = resolveTeamDefinitionObject(modalTeamDefinitionId)
    }
    modal.openForPlannerDrop({
      order,
      teamDefinitionId: modalTeamDefinitionId,
      startAt: moment(event.start).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
    })
  }

  const handleAssignedEventUpdate = () => {
    plannerData.fetchPlannerData()
  }

  const handleFirstAvailableUpdate = ({ event, oldEvent }) => {
    const teamDefinitions = resolveFirstAvailableDragTeamDefinitionIds({ event, oldEvent })
    if (!teamDefinitions.length) {
      plannerData.fetchPlannerData()
      return
    }
    if (eventHasTimeChange({ event, oldEvent })) {
      plannerData.applyOptimisticPlannerEvent(
        createFirstAvailableOptimisticEvent(event, teamDefinitions),
      )
      openPlanningModalForDragTimeChange(event, { teamDefinitionIds: teamDefinitions })
      return
    }
    if (hasFirstAvailableTeamChange({ event, oldEvent })) {
      const payload = createFirstAvailablePlanningPayload(event, teamDefinitions)
      if (!payload) {
        plannerData.fetchPlannerData()
        return
      }
      closeEventPopup()
      plannerData.applyOptimisticPlannerEvent(
        createFirstAvailableOptimisticEvent(event, teamDefinitions),
      )
      plannerData.savePlanning(event.original.orderId, payload)
      return
    }
    plannerData.fetchPlannerData()
  }

  const handleStandardEventUpdate = ({ event, oldEvent }) => {
    if (!eventHasMeaningfulChange({ event, oldEvent })) {
      return
    }
    closeEventPopup()
    const eventWithTeamDefinition = resolveEventWithSelectedTeamDefinition(event)
    if (eventHasTimeChange({ event, oldEvent })) {
      plannerData.applyOptimisticPlannerEvent(eventWithTeamDefinition)
      openPlanningModalForDragTimeChange(eventWithTeamDefinition)
      return
    }
    const payload = createPlanningPayloadFromEvent(eventWithTeamDefinition)
    if (!payload) {
      return
    }
    plannerData.applyOptimisticPlannerEvent(eventWithTeamDefinition)
    plannerData.savePlanning(eventWithTeamDefinition.original.orderId, payload)
  }

  const handleEventUpdated = ({ event, oldEvent }) => {
    if (!event?.original?.orderId) {
      return
    }
    if (event?.original?.isAssigned || oldEvent?.original?.isAssigned) {
      handleAssignedEventUpdate()
      return
    }
    if (event?.original?.isFirstAvailablePlanning || oldEvent?.original?.isFirstAvailablePlanning) {
      handleFirstAvailableUpdate({ event, oldEvent })
      return
    }
    handleStandardEventUpdate({ event, oldEvent })
  }

  const handleEventCreated = event => {
    const orderId = event?.original?.orderId
    const teamDefinitionId = Number(event?.resource || 0)
    const startAt = moment(event?.start)
    if (!orderId || !teamDefinitionId || !startAt.isValid()) {
      return
    }
    closeEventPopup()
    pendingPlannerDropOrder.value = {
      ...(event?.original?.original || {}),
      ...(event?.original || {}),
      id: event?.original?.id || event?.original?.orderId || orderId,
      orderId,
    }
    plannerData.fetchPlannerData()
    const modal = planningModal?.value
    if (!modal || typeof modal.openForPlannerDrop !== 'function') {
      return
    }
    modal.openForPlannerDrop({
      order: pendingPlannerDropOrder.value,
      teamDefinitionId,
      startAt: startAt.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
    })
  }

  const handleDurationChange = durationMinutes => {
    if (!selectedEvent.value || durationMinutes === null || durationMinutes === undefined || durationMinutes <= 0) {
      return
    }
    const payload = createDurationPatchPayload({
      event: selectedEvent.value,
      durationMinutes,
    })
    if (!payload) {
      return
    }
    const { orderId } = selectedEvent.value
    plannerData.applyOptimisticPlannerEvent({
      ...selectedEvent.value,
      original: { ...selectedEvent.value.original, durationMinutes },
    })
    plannerData.patchOrder(orderId, payload)
  }

  const handleAssignmentOffsetChange = assignmentOffsetMinutes => {
    if (!selectedEvent.value || assignmentOffsetMinutes === null || assignmentOffsetMinutes === undefined) {
      return
    }
    const payload = createAssignmentOffsetPatchPayload({
      event: selectedEvent.value,
      assignmentOffsetMinutes,
    })
    if (!payload) {
      return
    }
    const { orderId } = selectedEvent.value
    plannerData.applyOptimisticPlannerEvent({
      ...selectedEvent.value,
      original: { ...selectedEvent.value.original, bufferBefore: assignmentOffsetMinutes },
    })
    plannerData.patchOrder(orderId, payload)
  }

  const handlePopupSaveTeams = ({ orderId, payload }) => {
    plannerData.savePlanning(orderId, payload)
  }

  const removePlanning = orderId => {
    closeEventPopup()
    return plannerData.removePlanning(orderId)
  }

  const handlePlanningModalUpdated = ({
    orderId,
    teamDefinitionId,
    assignmentOffsetMinutes,
    durationMinutes,
    startAt,
    fromPlannerDrop,
  }) => {
    if (fromPlannerDrop) {
      const sourceOrder = pendingPlannerDropOrder.value
        || plannerData.plannerState.value.backlogItems.find(
          item => String(item.original?.orderId || item.id) === String(orderId),
        )?.original

      if (sourceOrder) {
        const optimisticOrder = createPlannerOrderWithOverrides({
          order: sourceOrder,
          teamDefinitions: plannerData.plannerState.value.teamDefinitions,
          teamDefinitionId,
          startAt,
          assignmentOffsetMinutes,
          durationMinutes,
        })
        plannerData.applyOptimisticPlannerEvent(mapPlannerOrderToEvent(optimisticOrder))
      }
    }
    dragTimePendingModal.value = false
    pendingPlannerDropOrder.value = null
    plannerData.fetchPlannerData()
  }

  const handlePlanningModalHidden = () => {
    if (dragTimePendingModal.value) {
      dragTimePendingModal.value = false
      plannerData.fetchPlannerData()
    }
  }

  return {
    selectedEvent,
    selectedEventAnchor,
    isEventPopupOpen,
    dragTimePendingModal,
    pendingPlannerDropOrder,
    handleEventClicked,
    handleEventCreated,
    handleEventUpdated,
    handleDurationChange,
    handleAssignmentOffsetChange,
    handlePopupSaveTeams,
    handlePlanningModalUpdated,
    handlePlanningModalHidden,
    closeEventPopup,
    removePlanning,
  }
}
